import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../../lib/api.js';
import { Callout } from '../../components/Callout.js';
import { QueryState } from '../../components/QueryState.js';

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function FilesTab({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();

  const files = useQuery({
    queryKey: ['files', projectId],
    queryFn: () => api.listFiles(projectId),
  });

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadFile(projectId, file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['files', projectId] });
    },
  });

  return (
    <div data-testid="files-tab">
      <p className="dh" style={{ fontSize: 22 }}>
        Files
      </p>
      <p className="mm" style={{ fontSize: 13, margin: '8px 0 16px', lineHeight: 1.6, maxWidth: '68ch' }}>
        Agreements, permits and location documentation. Files are kept: replacing one adds a new
        version rather than overwriting, and deleting one is recoverable.
      </p>

      <QueryState query={files} subject="files" testId="files" />

      <input
        type="file"
        data-testid="file-upload"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate(file);
        }}
      />

      {upload.isError ? (
        <div style={{ marginTop: 14 }}>
          <Callout tone="danger" title="Not uploaded" testId="file-error">
            {upload.error instanceof ApiError ? upload.error.message : 'Upload failed.'}
          </Callout>
        </div>
      ) : null}

      {files.data && files.data.length === 0 ? (
        <p className="mm" style={{ fontSize: 13, marginTop: 18 }} data-testid="files-empty">
          No files yet.
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: 8, marginTop: 18 }}>
        {files.data?.map((f) => (
          <div
            key={f.id}
            className="card1"
            style={{ padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
            data-testid={`file-${f.id}`}
          >
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>{f.originalFilename}</span>
            <span className="mm" style={{ fontSize: 11.5 }}>
              {humanSize(f.byteSize)}
            </span>
            {/* Quarantined until scanning passes; nothing is downloadable before
                then, and the UI says why rather than offering a dead link. */}
            {f.validationStatus !== 'PASSED' ? (
              <span className="stag" style={{ background: '#FDF2CC', color: '#B54708' }} data-testid={`quarantined-${f.id}`}>
                checking
              </span>
            ) : (
              <span className="stag" style={{ background: '#D1FADF', color: '#067647' }}>
                ready
              </span>
            )}
            <span className="mm" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
              {new Date(f.createdAt).toLocaleDateString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
